import * as Sentry from '@sentry/react';
import * as packageJson from '../package.json';
import {
  KUBERNETES_NAMESPACE,
  KUBERNETES_NODE_NAME,
  KUBERNETES_POD_IP,
  KUBERNETES_POD_NAME,
  LOCIZE_API_KEY,
  LOCIZE_PROJECT_ID,
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
} from './common/constants/env.constants';

import { AuthenticationProvider } from './authentication/authProvider';
import ChainedBackend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import Locize from 'i18next-locize-backend';
import { TaskPane } from './taskpane/TaskPane';
import { createRoot } from 'react-dom/client';
import enCommon from './locales/en/common.json';
import enTransaction from './locales/en/transaction.json';
import enNote from './locales/en/note.json';
import enMatter from './locales/en/matter.json';
import fiCommon from './locales/fi/common.json';
import enTask from './locales/en/task.json';
import enWorkTime from './locales/en/worktime.json';
import enConflict from './locales/en/conflict.json';
import enMatterRequest from './locales/en/matterRequest.json';
import enCustomer from './locales/en/customer.json';
import fiMatter from './locales/fi/matter.json';
import fiTransaction from './locales/fi/transaction.json';
import fiTask from './locales/fi/task.json';
import fiNote from './locales/fi/note.json';
import fiWorkTime from './locales/fi/worktime.json';
import fiConflict from './locales/fi/conflict.json';
import fiMatterRequest from './locales/fi/matterRequest.json';
import fiCustomer from './locales/fi/customer.json';
import enCustomerIdentification from './locales/en/customerIdentification.json';
import svCustomerIdentification from './locales/sv/customerIdentification.json';
import fiCustomerIdentification from './locales/fi/customerIdentification.json';
import enPreliminaryInvoice from './locales/en/preliminaryInvoice.json';
import fiPreliminaryInvoice from './locales/fi/preliminaryInvoice.json';
import svPreliminaryInvoice from './locales/sv/preliminaryInvoice.json';
import enExpense from './locales/en/expense.json';
import svExpense from './locales/sv/expense.json';
import fiExpense from './locales/fi/expense.json';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import svCommon from './locales/sv/common.json';
import svTransaction from './locales/sv/transaction.json';
import svNote from './locales/sv/note.json';
import svMatter from './locales/sv/matter.json';
import svTask from './locales/sv/task.json';
import svWorkTime from './locales/sv/worktime.json';
import svConflict from './locales/sv/conflict.json';
import svMatterRequest from './locales/sv/matterRequest.json';
import svCustomer from './locales/sv/customer.json';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UserProvider } from './context/UserContext';
import { LanguageProvider } from './context/LanguageContext';

import { Suspense } from 'react';
import { LoadingProvider } from './context/LoadingContext';
import GlobalLoader from './components/GlobalLoader';
import ToastRegion from './components/toastRegion/toastRegion';
import 'react-toastify/dist/ReactToastify.css';
import { TransactionProvider } from './context/TransactionContext';
import { TransactionParameterProvider } from './context/TransactionParameterContext';
import { TransactionRestrictionsProvider } from './context/TransactionRestrictionsContext';
import { TaskProvider } from './context/TaskContext';
import { ThemeProvider } from './components/context/ThemeContext';
import GlobalStyle from './components/config/global.style';
import { Spin } from './components/Ant';
import { TimerProvider } from './context/TimerContext';
import { ConflictCheckProvider } from './context/ConflictCheckContext';
import { MatterRequestProvider } from './context/MatterRequestContext';
import { FeatureProvider } from './context/FeatureContext';
import { WorkTimeProvider } from './context/WorkTimeContext';
import { DraggableButtonProvider } from './context/DraggableButtonContext';
import AppVersionSetter from './components/appUpdate/appVersionSetter';
import ErrorPage from './components/error/error';
import { LOCAL_STORAGE_NAMES } from './constants/constants';
import { ManageMatterRequestProvider } from './context/ManageMatterRequest';
import TimezoneInfo from './components/timezoneInfo/timeZoneInfo';
import { SystemInfoProvider } from './context/SystemInfoContext';
import { shouldIgnoreSentryEvent } from './utils/sentryErrorFilter';
import { isMsalPopupLanding, releaseMsalPopupLanding } from './utils/msalPopupLanding';
import { initScrollbarVisibility } from './utils/scrollbarVisibility';
import { applyDisplaySize, readStoredDisplaySize } from './utils/displaySize';
import { applyThemeColor, readStoredThemeColor } from './utils/themeColor';
import { readStoredUserId } from './utils/userPreferences';
import { DisplaySizeProvider } from './context/DisplaySizeContext';
import { ThemeColorProvider } from './context/ThemeColorContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

initScrollbarVisibility();
const storedUserId = readStoredUserId();
applyDisplaySize(readStoredDisplaySize(storedUserId));
applyThemeColor(readStoredThemeColor(storedUserId));

const bundledTranslationResources = {
  en: {
    common: enCommon,
    transaction: enTransaction,
    note: enNote,
    matter: enMatter,
    task: enTask,
    worktime: enWorkTime,
    conflict: enConflict,
    matterRequest: enMatterRequest,
    customer: enCustomer,
    customerIdentification: enCustomerIdentification,
    expense: enExpense,
    preliminaryInvoice: enPreliminaryInvoice,
  },
  fi: {
    common: fiCommon,
    transaction: fiTransaction,
    note: fiNote,
    matter: fiMatter,
    task: fiTask,
    worktime: fiWorkTime,
    conflict: fiConflict,
    matterRequest: fiMatterRequest,
    customer: fiCustomer,
    customerIdentification: fiCustomerIdentification,
    expense: fiExpense,
    preliminaryInvoice: fiPreliminaryInvoice,
  },
  sv: {
    common: svCommon,
    transaction: svTransaction,
    note: svNote,
    matter: svMatter,
    task: svTask,
    worktime: svWorkTime,
    conflict: svConflict,
    matterRequest: svMatterRequest,
    customer: svCustomer,
    customerIdentification: svCustomerIdentification,
    expense: svExpense,
    preliminaryInvoice: svPreliminaryInvoice,
  },
};

i18n
  .use(ChainedBackend)
  .use(initReactI18next)
  .init({
    ns: [
      'common',
      'onboarding',
      'transaction',
      'note',
      'matter',
      'task',
      'worktime',
      'conflict',
      'matterRequest',
      'customer',
      'customerIdentification',
      'expense',
      'preliminaryInvoice',
    ],
    saveMissing: false,
    preload: ['en', 'fi', 'sv'],
    lng: 'en',
    fallbackLng: 'en',
    returnNull: false,
    backend: {
      backends: [
        LocalStorageBackend, // Use cached translations
        Locize, // When cache is outdated/empty, fetch from Locize service
        resourcesToBackend(bundledTranslationResources), // As last resort, use bundled translation from json files
      ],
      backendOptions: [
        {
          expirationTime: 1 * 24 * 60 * 60 * 1000, // 1 day
        },
        {
          projectId: LOCIZE_PROJECT_ID,
          apiKey: LOCIZE_API_KEY,
          referenceLng: 'en',
          version: 'latest',
          private: false,
          cdnType: 'pro',
        },
      ],
    },
    react: {
      useSuspense: true,
    },
  });

Sentry.init({
  enabled: true,
  dsn: SENTRY_DSN,
  release: `${packageJson.name}@${packageJson.version}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  environment: SENTRY_ENVIRONMENT,
  debug: SENTRY_ENVIRONMENT === 'development',
  sampleRate: 1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  maxBreadcrumbs: 100,
  beforeSend: function (event, hint) {
    if (shouldIgnoreSentryEvent(event, hint)) {
      return null;
    }
    event.tags = {
      ...event.tags,
    };
    const currentOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
    const { organizationUrl, organizationName } = currentOrganisation
      ? JSON.parse(currentOrganisation)
      : {};
    const k8sEnv = (globalThis as typeof globalThis & { __K8S_ENV__?: Record<string, string> })
      .__K8S_ENV__;
    event.contexts = {
      ...event.contexts,
      Kubernetes: {
        namespace: k8sEnv?.namespace || KUBERNETES_NAMESPACE,
        pod_name: k8sEnv?.podName || KUBERNETES_POD_NAME,
        pod_ip: k8sEnv?.podIp || KUBERNETES_POD_IP,
        node_name: k8sEnv?.nodeName || KUBERNETES_NODE_NAME,
      },
      ...(organizationName && {
        'Organization Details': {
          organization_name: organizationName,
          organization_url: organizationUrl,
        },
      }),
    };
    return event;
  },
});
let root: ReturnType<typeof createRoot> | null = null;

const render = (host: Office.HostType) => {
  const container = document.getElementById('container');
  if (container) {
    if (!root) {
      root = createRoot(container); // Create root only once
    }

    root.render(
      <Suspense fallback={<Spin fullscreen />}>
        <ThemeProvider>
          <GlobalStyle />
          <Sentry.ErrorBoundary fallback={<ErrorPage isException={true} />}>
            <ToastRegion />
            <LoadingProvider>
              <QueryClientProvider client={queryClient}>
                <DraggableButtonProvider key="draggable-button-provider">
                  <TransactionRestrictionsProvider key="transaction-restrictions-provider">
                    <TransactionParameterProvider key="transaction-parameter-provider">
                      <LanguageProvider key="language-provider">
                        <UserProvider key="user-provider">
                          <DisplaySizeProvider key="display-size-provider">
                            <ThemeColorProvider key="theme-color-provider">
                              <SystemInfoProvider key="system-info-provider">
                                <TransactionProvider key="transaction-provider">
                                  <TaskProvider key="task-provider">
                                    <ManageMatterRequestProvider key="manage-matter-request-provider">
                                      <ConflictCheckProvider key="conflict-check-provider">
                                        <MatterRequestProvider key="matter-request-provider">
                                          <TimerProvider key="timer-provider">
                                            <WorkTimeProvider key="work-time-provider">
                                              <AuthenticationProvider key="auth-provider">
                                                <FeatureProvider key="feature-provider">
                                                  <GlobalLoader />
                                                  <TimezoneInfo />
                                                  <AppVersionSetter />
                                                  <TaskPane host={host} key={'task-pane'} />
                                                </FeatureProvider>
                                              </AuthenticationProvider>
                                            </WorkTimeProvider>
                                          </TimerProvider>
                                        </MatterRequestProvider>
                                      </ConflictCheckProvider>
                                    </ManageMatterRequestProvider>
                                  </TaskProvider>
                                </TransactionProvider>
                              </SystemInfoProvider>
                            </ThemeColorProvider>
                          </DisplaySizeProvider>
                        </UserProvider>
                      </LanguageProvider>
                    </TransactionParameterProvider>
                  </TransactionRestrictionsProvider>
                </DraggableButtonProvider>
              </QueryClientProvider>
            </LoadingProvider>
          </Sentry.ErrorBoundary>
        </ThemeProvider>
      </Suspense>,
    );
  }
};

if (isMsalPopupLanding(globalThis.location.hash)) {
  releaseMsalPopupLanding(globalThis.window);
} else {
  Office.onReady((info) => {
    render(info.host);
  });
  Office.initialize = () => {
    render(Office.HostType.Outlook);
  };
}
